import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import Icon from '../Icon'
import './ui.css'

function normalizeOption(option) {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
      description: '',
      searchText: option,
    }
  }

  const value = String(option?.value ?? '')
  const label = String(option?.label ?? value)
  const description = String(option?.description ?? '')
  return {
    ...option,
    value,
    label,
    description,
    searchText: String(option?.searchText ?? `${label} ${description}`),
  }
}

function SearchableSelect({
  id,
  label,
  value = '',
  options = [],
  placeholder = '',
  emptyText = 'Ничего не найдено',
  disabled = false,
  className = '',
  onChange,
}) {
  const generatedId = useId().replace(/:/g, '')
  const inputId = id || `searchable-select-${generatedId}`
  const listboxId = `${inputId}-listbox`
  const inputRef = useRef(null)
  const rootRef = useRef(null)
  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options])
  const selectedOption = normalizedOptions.find((option) => option.value === String(value)) || null
  const [inputValue, setInputValue] = useState(selectedOption?.label || '')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!open) {
      setInputValue(selectedOption?.label || '')
    }
  }, [open, selectedOption?.label])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const filteredOptions = useMemo(() => {
    const query = inputValue.trim().toLowerCase()
    if (!query) return normalizedOptions
    return normalizedOptions.filter((option) => option.searchText.toLowerCase().includes(query))
  }, [inputValue, normalizedOptions])

  const safeActiveIndex = Math.min(activeIndex, Math.max(filteredOptions.length - 1, 0))

  const selectOption = (option) => {
    setInputValue(option.label)
    setOpen(false)
    onChange?.(option.value, option)
  }

  const clearSelectedValueIfNeeded = (nextValue) => {
    if (!selectedOption) return
    if (nextValue === selectedOption.label) return
    onChange?.('', null)
  }

  const handleInputChange = (event) => {
    const nextValue = event.target.value
    setInputValue(nextValue)
    setOpen(true)
    setActiveIndex(0)
    clearSelectedValueIfNeeded(nextValue)
  }

  const handleInputFocus = (event) => {
    setOpen(true)
    event.target.select()
  }

  const handleToggleMouseDown = (event) => {
    event.preventDefault()
    if (disabled) return
    setInputValue('')
    setOpen(true)
    setActiveIndex(0)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event) => {
    if (disabled) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActiveIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActiveIndex((index) => Math.max(index - 1, 0))
      return
    }

    if (event.key === 'Enter' && open && filteredOptions[safeActiveIndex]) {
      event.preventDefault()
      selectOption(filteredOptions[safeActiveIndex])
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className={`ui-searchable-select${className ? ` ${className}` : ''}`}>
      {label && (
        <label className="ui-searchable-select__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="ui-searchable-select__control">
        <input
          ref={inputRef}
          id={inputId}
          className="ui-input ui-searchable-select__input"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open && filteredOptions[safeActiveIndex] ? `${listboxId}-${safeActiveIndex}` : undefined}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="ui-searchable-select__toggle"
          aria-label={open ? 'Свернуть список' : 'Показать список'}
          disabled={disabled}
          onMouseDown={handleToggleMouseDown}
        >
          <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
        </button>
      </div>
      {open && (
        <ul id={listboxId} className="ui-searchable-select__list" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value}
                id={`${listboxId}-${index}`}
                className={`ui-searchable-select__option${index === safeActiveIndex ? ' is-active' : ''}`}
                role="option"
                aria-selected={option.value === String(value)}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault()
                  selectOption(option)
                }}
              >
                <span className="ui-searchable-select__option-label">{option.label}</span>
                {option.description && (
                  <span className="ui-searchable-select__option-description">{option.description}</span>
                )}
              </li>
            ))
          ) : (
            <li className="ui-searchable-select__empty">{emptyText}</li>
          )}
        </ul>
      )}
    </div>
  )
}

export default SearchableSelect
